import * as React from "react";
import { ChatBubbleBottomCenterIcon } from "@heroicons/react/24/outline";
import { ClipboardIcon, MoreHorizontal } from "lucide-react";
import { DriveRowMenu } from "./driveRowMenu";
import { useFeatureAccess } from "@/hooks/use-feature-access";
import { useRecentItems } from "@/hooks/use-add-recent-items";
import { Button } from "@/components/ui/button";
import { FolderItem, Item } from "@/types/fse";
import { Link } from "react-router";
import { DriveRowProgressbar } from "./driveRowProgressbar";
import { calculateUploadProgress } from "../modals/uploadModal/helpers";
import { DriveRowButtonsChronology } from "@/views/drive/components/driveRowButtonsChronology";
import { TxType } from "@/types/transaction";

interface Props {
  item: Item;
  progressForItem: Record<string, [number, TxType]>;
  parentFolder: FolderItem | null;
  hiddenButtons?: Array<string>;
}

interface DummyLinkProps {
  to: string;
  children: React.ReactNode;
}

const DummyLink: React.FC<DummyLinkProps> = ({ children }) => <>{children}</>;

export const DriveRowButtons: React.FC<Props> = ({ item, progressForItem, parentFolder, hiddenButtons = [] }) => {
  const { hasAccess } = useFeatureAccess();
  let progressLabel: string = "";

  const { addRecentItem } = useRecentItems();

  const transactions = progressForItem[item.id]
    ? [progressForItem[item.id]]
    : [
        typeof item.transactions?.chronology?.progress !== "undefined"
          ? [item.transactions?.chronology?.progress || 0.01, TxType.CHRONOLOGY]
          : [],
        typeof item.transactions?.ingestion?.progress !== "undefined"
          ? [item.transactions?.ingestion?.progress || 0.01, TxType.UPLOAD]
          : [],
      ]
        .filter(row => row.length)
        .sort(row => row[0]);

  const progressStatus = transactions[0];
  const progress = progressStatus ? progressStatus[0] || 0 : 1;

  const handleQAClick = () => {
    addRecentItem(item);
  };

  const isProcessed = !progressForItem[item.id] || progress >= 1.0;

  const LinkWrapper = isProcessed ? Link : DummyLink;

  if (progressStatus) {
    if (progressStatus[1] === TxType.UPLOAD) {
      progressLabel = "Uploading";
    } else if (progressStatus[1] === TxType.CHRONOLOGY) {
      progressLabel = "Processing";
    }
  }

  return (
    <div className="mt-2 flex w-full flex-wrap items-center justify-start gap-2 md:mt-0 md:w-1/2 md:justify-end lg:w-6/12">
      {progress > 0 && progress < 1 && (
        <DriveRowProgressbar progressLabel={progressLabel} progress={calculateUploadProgress(progress)} />
      )}

      {hasAccess("reports") && !hiddenButtons.includes("snapShot") && (
        <Button
          variant="secondary"
          size="sm"
          className="w-full text-xs sm:w-auto"
          asChild={isProcessed}
          disabled={!isProcessed}
        >
          <LinkWrapper to={`/reports/${item.id}`}>
            <ClipboardIcon className="mr-1 h-4 w-4" />
            SnapShot
          </LinkWrapper>
        </Button>
      )}
      {hasAccess("q_and_a") && !hiddenButtons.includes("qa") && (
        <Button
          variant="secondary"
          size="sm"
          className="w-full text-xs sm:w-auto"
          onClick={handleQAClick}
          asChild={isProcessed}
          disabled={!isProcessed}
        >
          <LinkWrapper to={`/chat/${item.id}`}>
            <ChatBubbleBottomCenterIcon className="mr-1 h-4 w-4" />
            Q&A
          </LinkWrapper>
        </Button>
      )}

      {!hiddenButtons.includes("chronology") && (
        <DriveRowButtonsChronology parentFolder={parentFolder} item={item} isDisabled={!isProcessed} />
      )}
      {!hiddenButtons.includes("options") && (
        <DriveRowMenu variant="empty" item={item} isFolder={item.type === "folder"}>
          <MoreHorizontal className="h-4 w-4" />
        </DriveRowMenu>
      )}
    </div>
  );
};
